import {useTranslation} from 'react-i18next';
import Accordion from 'web-library/stories/atom/Accordion/Accordion';
import Grid from 'web-library/stories/atom/Grid/Grid';
import Checkbox from 'web-library/stories/atom/Checkbox/Checkbox';
import Typography from 'web-library/stories/atom/Typography/Typography';
import {alphanumericRegExp, Button, numericRegExp, TextFieldForm} from 'web-library';
import React, {useState} from 'react';
import {FormControlLabel, Radio, RadioGroup} from '@mui/material';
import {Controller, UseFormReturn} from 'react-hook-form';
import {useStyles} from './PersonalParticulars';
import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
} from '../../../../models';
import {ContactNumberFormItem} from '../../../../components/form-items/ContactNumberFormItem';
import {getIExamMasterCodeSelected} from '../../logic/useCreateCandidateForm';
import LocalAddressHelper from '../../../../components/helper-text/LocalAddressHelper';
import {prefix0For2Digits} from '../../../../utils/StringUtils';
import {useGetAddressDetailsByPostcode} from '../../logic/useGetAddressDetailsByPostcode';
import ControlledFormControlLabel
  from "web-library/stories/atom/FormControlLabel/ControlledFormControlLabel";

interface ContactInfoAddressProps {
  formHook: UseFormReturn<CandidateProfileFormData>;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  isSingPassUser?: boolean;
}

export interface ContactInformationProps extends ContactInfoAddressProps {
  isIExamsApiEnabled: boolean;
}

interface ContactInfoMobileProps {
  formHook: UseFormReturn<CandidateProfileFormData>;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  isIExamsApiEnabled?: boolean;
}

const ContactInfoMobile = ({
                             formHook,
                             isDisableInputs,
                             isIExamsApiEnabled = true,
                           }: ContactInfoMobileProps) => {
  const {t} = useTranslation();
  const {classes} = useStyles();
  const {formState: {errors}, control} = formHook;
  return (
      <Grid container spacing={1} direction="row" className={classes.textField}>
        <Controller
            name="preferredTelephoneOrMobile"
            control={control}
            defaultValue="M"
            render={({field}) =>
                <RadioGroup
                    {...field}
                    className={`${classes.groupMultirowContainer} ${classes.responsiveFlexContainer}`}
                >
                  <ContactNumberFormItem
                      containerClassName={classes.groupFormElementContainer}
                      id="candidateProfileMobile"
                      formHook={formHook}
                      label={t('profile->>CreateCandidateForm.MobileNo')}
                      isRequired
                      placeholder="Enter mobile no."
                      countryCode={{
                        name: 'mobileCountryCode',
                        error: !!errors.mobileCountryCode,
                        helperText: errors?.mobileCountryCode?.message?.toString(),
                        disabled: isDisableInputs?.mobileCountryCode,
                      }}
                      countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
                      number={{
                        name: 'mobileContactNumber',
                        error: !!errors.mobileContactNumber,
                        helperText: errors?.mobileContactNumber?.message?.toString(),
                        disabled: isDisableInputs?.mobileContactNumber,
                      }}
                      isSkipTransform
                      isIExamsApiEnabled={isIExamsApiEnabled}
                  />

                  <FormControlLabel
                      className={classes.formElementContainer}
                      value="M"
                      name="preferredTelephoneOrMobile"
                      control={<Radio value="M"/>}
                      label="Set as preferred contact"
                      disabled={isDisableInputs?.preferredTelephoneOrMobile}
                  />
                  <ContactNumberFormItem
                      containerClassName={classes.groupFormElementContainer}
                      id="candidateProfileTelephone"
                      formHook={formHook}
                      label={t('profile->>CreateCandidateForm.TelephoneNo')}
                      countryCode={{
                        name: 'telephoneCountryCode',
                        error: !!errors.telephoneCountryCode,
                        helperText: errors?.telephoneCountryCode?.message?.toString(),
                        disabled: isDisableInputs?.telephoneCountryCode,
                      }}
                      countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
                      number={{
                        name: 'telephoneContactNumber',
                        error: !!errors.telephoneContactNumber,
                        helperText: errors?.telephoneContactNumber?.message?.toString(),
                        disabled: isDisableInputs?.telephoneContactNumber,
                      }}
                      isSkipTransform
                      placeholder="Enter phone no."
                      isIExamsApiEnabled={isIExamsApiEnabled}
                  />
                  <FormControlLabel
                      control={<Radio/>}
                      value="T"
                      name="preferredTelephoneOrMobile"
                      label="Set as preferred contact"
                      disabled={isDisableInputs?.preferredTelephoneOrMobile}
                  />
                </RadioGroup>
            }/>
      </Grid>
  );
};

const isRegisteredAddressIncomplete = (formDefaultValues?: CandidateProfileFormDefaultValues) =>
    !formDefaultValues?.houseOrBlkNo ||
    !formDefaultValues?.streetName ||
    !formDefaultValues?.postalCode;

interface ForeignAddressProps {
  formHook: UseFormReturn<CandidateProfileFormData>;
  classes: Record<string,string>;
  isDisableInputs?: CandidateProfileIsDisableInputs;
}

const ForeignAddress = ({formHook, classes, isDisableInputs}: ForeignAddressProps) => {
  const {t} = useTranslation();
  const {setValue, formState: {errors}} = formHook;
  return (
      <>
        <TextFieldForm
            form={formHook}
            isInputTextUpperCase
            onChange={(e) => setValue('foreignAddress1', e.target.value.toUpperCase())}
            label="Foreign Address"
            placeholder="Address Line 1"
            name="foreignAddress1"
            type="text"
            error={!!errors.foreignAddress1}
            helperText={errors?.foreignAddress1?.message?.toString()}
            fullWidth
            className={classes.textFieldSmallPaddingBottom}
            disabled={isDisableInputs?.foreignAddress1}
            isSubLabelBelowLabel
            subLabel={
              <Typography variant="body2">{t('profile->>AddressHelper.ForeignAddress')}</Typography>
            }
        />
        <TextFieldForm
            form={formHook}
            isInputTextUpperCase
            onChange={(e) => setValue('foreignAddress2', e.target.value.toUpperCase())}
            placeholder="Address Line 2"
            name="foreignAddress2"
            type="text"
            error={!!errors.foreignAddress2}
            helperText={errors?.foreignAddress2?.message?.toString()}
            fullWidth
            className={classes.textFieldSmallPaddingBottom}
            disabled={isDisableInputs?.foreignAddress2}
        />
        <TextFieldForm
            form={formHook}
            isInputTextUpperCase
            onChange={(e) => setValue('foreignAddress3', e.target.value.toUpperCase())}
            placeholder="Address Line 3"
            name="foreignAddress3"
            type="text"
            error={!!errors.foreignAddress3}
            helperText={errors?.foreignAddress3?.message?.toString()}
            fullWidth
            className={classes.textFieldSmallPaddingBottom}
            disabled={isDisableInputs?.foreignAddress3}
        />
      </>
  );
};

const ContactInfoAddress = ({
                              formHook,
                              formDefaultValues,
                              isDisableInputs,
                              isSingPassUser,
                            }: ContactInfoAddressProps) => {
  const {t} = useTranslation();
  const {classes} = useStyles();
  const {
    formState: {errors},
    control,
    setValue,
    getValues,
    trigger,
    setError,
    clearErrors
  } = formHook;

  const [sameAsRegisteredAddress, setSameAsRegisteredAddress] = useState<boolean>(() => {
    if (!isRegisteredAddressIncomplete(formDefaultValues)) {
      return formDefaultValues?.sameAsRegisteredAddress === 'R';
    }
    return false;
  });

  const handleSameAsRegisteredAddress = () => {
    setSameAsRegisteredAddress(!sameAsRegisteredAddress);
  };

  const {
    isDisableAddr,
    validatePostalCode,
    isApiError,
    isPostalCodeEmpty,
    postcodeOnChange,
  } = useGetAddressDetailsByPostcode<CandidateProfileFormData>({
    blkNoInputName: 'houseOrBlkNo',
    streetInputName: 'streetName',
    buildingInputName: 'building',
    postcodeInputName: 'postalCode',
    isPostcodeEmptyDefaultValue: true,
    setValue,
    getValues,
    setError,
    clearErrors,
    trigger,
  });

  return (
      <>
        <LocalAddressHelper className={classes.textFieldSmallPaddingBottom}/>
        <Controller
            name="sameAsRegisteredAddress"
            control={control}
            render={({field}) =>
                <FormControlLabel
                    control={<Checkbox/>}
                    className={classes.textFieldSmallPaddingBottom}
                    name="sameAsRegisteredAddress"
                    label="Same as Registered Address"
                    disabled={
                        isDisableInputs?.sameAsRegisteredAddress ||
                        isRegisteredAddressIncomplete(formDefaultValues)
                    }
                    onChange={handleSameAsRegisteredAddress}
                />
            }
        />

        <Controller
            name="preferredAddress"
            control={control}
            defaultValue="L"
            render={({field}) =>
                <RadioGroup {...field}>
                  <TextFieldForm
                      className={classes.textFieldSmallPaddingBottom}
                      isInputTextUpperCase
                      onChange={(e) => setValue('houseOrBlkNo', e.target.value.toUpperCase())}
                      isRequired
                      id="inputText-residentialAddress-blkNo"
                      form={formHook}
                      placeholder="House/Block No."
                      name="houseOrBlkNo"
                      type="text"
                      label={t('profile->>CreateCandidateForm.SingaporeMailingAddress')}
                      error={!!errors.houseOrBlkNo}
                      helperText={errors?.houseOrBlkNo?.message?.toString()}
                      fullWidth
                      disabled={isDisableInputs?.houseOrBlkNo || sameAsRegisteredAddress}
                      inputProps={{
                        disabled: isDisableInputs?.houseOrBlkNo || sameAsRegisteredAddress,
                        readOnly: sameAsRegisteredAddress || isDisableAddr,
                      }}
                  />
                  <TextFieldForm
                      className={classes.textFieldSmallPaddingBottom}
                      isInputTextUpperCase
                      onChange={(e) => setValue('streetName', e.target.value.toUpperCase())}
                      id="inputText-residentialAddress-streetName"
                      form={formHook}
                      placeholder="Street Name"
                      name="streetName"
                      type="text"
                      error={!!errors.streetName}
                      helperText={errors?.streetName?.message?.toString()}
                      fullWidth
                      inputProps={{
                        disabled: isDisableInputs?.streetName,
                        readOnly: sameAsRegisteredAddress || isDisableAddr,
                      }}
                  />
                  <TextFieldForm
                      className={classes.textFieldSmallPaddingBottom}
                      isInputTextUpperCase
                      onChange={(e) => setValue('building', e.target.value.toUpperCase())}
                      id="inputText-residentialAddress-building"
                      form={formHook}
                      placeholder={t('profile->>AddressHelper.BuildingPlaceholder')}
                      name="building"
                      type="text"
                      error={!!errors.building}
                      helperText={errors?.building?.message?.toString()}
                      fullWidth
                      inputProps={{
                        disabled: isDisableInputs?.building,
                        readOnly: sameAsRegisteredAddress || isDisableAddr,
                      }}
                  />
                  <Grid container spacing={1} direction="row"
                        className={classes.textFieldSmallPaddingBottom}>
                    <Grid md={6} sm={12} xs={12}>
                      <TextFieldForm
                          id="inputText-residentialAddress-floor"
                          isInputTextUpperCase
                          onChange={(e) => setValue('floor', e.target.value.toUpperCase())}
                          form={formHook}
                          placeholder={t('profile->>AddressHelper.FloorPlaceholder')}
                          name="floor"
                          type="text"
                          error={!!errors.floor}
                          helperText={errors?.floor?.message?.toString()}
                          fullWidth
                          inputProps={{
                            disabled: isDisableInputs?.floor,
                            readOnly: sameAsRegisteredAddress,
                            startAdornment: '#',
                          }}
                          onBlur={(e) => formHook?.setValue('floor', prefix0For2Digits(e.target.value))}
                          regExpToBlock={numericRegExp}
                      />
                    </Grid>

                    <Grid md={6} sm={12} xs={12}>
                      <TextFieldForm
                          id="inputText-residentialAddress-unit"
                          isInputTextUpperCase
                          onChange={(e) => setValue('unit', e.target.value.toUpperCase())}
                          placeholder={t('profile->>AddressHelper.UnitPlaceholder')}
                          name="unit"
                          type="text"
                          error={!!errors.unit}
                          helperText={errors?.unit?.message?.toString()}
                          fullWidth
                          inputProps={{
                            disabled: isDisableInputs?.unit,
                            readOnly: sameAsRegisteredAddress,
                            startAdornment: '-',
                          }}
                          regExpToBlock={alphanumericRegExp}
                          disabled={isDisableAddr}
                          form={formHook}
                      />
                    </Grid>
                  </Grid>
                  <div>
                    <TextFieldForm
                        isInputTextUpperCase
                        onChange={(e) => {
                          postcodeOnChange(e.target.value);
                        }}
                        className={`${classes.textFieldSmallPaddingBottom} ${classes.getAddressButton}`}
                        id="inputText-residentialAddress-postalCode"
                        form={formHook}
                        placeholder="Postal Code"
                        name="postalCode"
                        type="text"
                        error={!!errors.postalCode}
                        helperText={errors?.postalCode?.message?.toString()}
                        inputProps={{
                          disabled: isDisableInputs?.postalCode,
                          readOnly: sameAsRegisteredAddress,
                          startAdornment: 'Singapore',
                        }}
                        regExpToBlock={numericRegExp}
                    />
                    <Button
                        id="button-validate-postcode"
                        size="small"
                        variant="outlined"
                        onClick={validatePostalCode}
                        disabled={isPostalCodeEmpty}
                    >
                      {t('profile->>CandidateProfileDetail.GetAddress')}
                    </Button>
                    {isApiError && (
                        <Typography color="error">
                          {t('profile->>CandidateProfileDetail.PostcodeApiErrorMsg')}
                        </Typography>
                    )}
                  </div>

                  <FormControlLabel
                      control={<Radio/>}
                      className={classes.textField}
                      value="L"
                      name="preferredAddress"
                      label="Set as preferred address"
                      disabled={isDisableInputs?.preferredAddress}
                  />
                  <ForeignAddress formHook={formHook} classes={classes}
                                  isDisableInputs={isDisableInputs}/>
                  <FormControlLabel
                      control={<Radio/>}
                      value="F"
                      name="preferredAddress"
                      label="Set as preferred address"
                      disabled={isDisableInputs?.preferredAddress}
                  />
                </RadioGroup>
            }
        />
      </>

  );
};
const ContactInformation = ({
                              formHook,
                              formDefaultValues,
                              isDisableInputs,
                              isSingPassUser,
                              isIExamsApiEnabled = true,
                            }: ContactInformationProps) => {
  const {t} = useTranslation();
  const {classes} = useStyles();
  const {formState: {errors}, setValue} = formHook;
  const preventCutCopyPasteEmail = (e: React.SyntheticEvent<EventTarget>) => e.preventDefault();

  return (
      <Accordion
          defaultExpanded
          header={t('profile->>CreateCandidateForm.ContactInformation')}
          bottomLine
      >
        <TextFieldForm
            id="inputText-candidateProfile-email"
            label={t('profile->>CreateCandidateForm.EmailAddress')}
            isRequired
            placeholder="Enter email address"
            name="email"
            type="text"
            error={!!errors.email || !!errors.reEnterEmail}
            helperText={errors?.email?.message?.toString() || errors?.reEnterEmail?.message?.toString()}
            fullWidth
            className={classes.textField}
            onCut={preventCutCopyPasteEmail}
            onCopy={preventCutCopyPasteEmail}
            onPaste={preventCutCopyPasteEmail}
            disabled={isDisableInputs?.email}
            form={formHook}
        />
        <TextFieldForm
            form={formHook}
            label={t('profile->>CreateCandidateForm.ReEnterEmailAddress')}
            isRequired
            placeholder="Re-enter email address"
            name="reEnterEmail"
            size="medium"
            type="text"
            error={!!errors.email || !!errors.reEnterEmail}
            helperText={errors?.email?.message?.toString() || errors?.reEnterEmail?.message?.toString()}
            className={classes.textField}
            onCut={preventCutCopyPasteEmail}
            onCopy={preventCutCopyPasteEmail}
            onPaste={preventCutCopyPasteEmail}
            disabled={isDisableInputs?.reEnterEmail}
        />
        <ContactInfoMobile
            formHook={formHook}
            isDisableInputs={isDisableInputs}
            isIExamsApiEnabled={isIExamsApiEnabled}
        />

        {isSingPassUser && (
            <>
              <TextFieldForm
                  className={classes.textFieldSmallPaddingBottom}
                  isInputTextUpperCase
                  onChange={(e) => setValue('houseOrBlkNo', e.target.value.toUpperCase())}
                  label={t('profile->>CreateCandidateForm.RegisteredAddress')}
                  placeholder="House/Block No."
                  defaultValue={formDefaultValues?.houseOrBlkNo}
                  type="text"
                  fullWidth
                  disabled
                  form={formHook}
                  name="houseOrBlkNo"
              />
              <TextFieldForm
                  className={classes.textFieldSmallPaddingBottom}
                  isInputTextUpperCase
                  onChange={(e) => setValue('streetName', e.target.value.toUpperCase())}
                  placeholder="Street Name"
                  defaultValue={formDefaultValues?.streetName}
                  type="text"
                  fullWidth
                  disabled
                  form={formHook}
                  name="streetName"
              />
              <TextFieldForm
                  className={classes.textFieldSmallPaddingBottom}
                  isInputTextUpperCase
                  onChange={(e) => setValue('building', e.target.value.toUpperCase())}
                  placeholder={t('profile->>AddressHelper.BuildingPlaceholder')}
                  defaultValue={formDefaultValues?.building}
                  type="text"
                  fullWidth
                  disabled
                  form={formHook}
                  name="building"
              />
              <Grid
                  container
                  spacing={1}
                  direction="row"
                  className={classes.textFieldSmallPaddingBottom}
              >
                <Grid md={6} sm={12} xs={12}>
                  <TextFieldForm
                      placeholder={t('profile->>AddressHelper.FloorPlaceholder')}
                      defaultValue={formDefaultValues?.floor}
                      type="text"
                      fullWidth
                      disabled
                      inputProps={{
                        startAdornment: '#',
                      }}
                      regExpToBlock={numericRegExp}
                      form={formHook}
                      name="floor"
                  />
                </Grid>
                <Grid md={6} sm={12} xs={12}>
                  <TextFieldForm
                      placeholder={t('profile->>AddressHelper.UnitPlaceholder')}
                      defaultValue={formDefaultValues?.unit}
                      type="text"
                      fullWidth
                      disabled
                      inputProps={{
                        startAdornment: '-',
                      }}
                      regExpToBlock={alphanumericRegExp}
                      form={formHook}
                      name="unit"
                  />
                </Grid>
              </Grid>
              <TextFieldForm
                  className={classes.textField}
                  placeholder="Postal Code"
                  defaultValue={formDefaultValues?.postalCode}
                  type="text"
                  fullWidth
                  disabled
                  inputProps={{
                    startAdornment: 'Singapore',
                  }}
                  regExpToBlock={numericRegExp}
                  form={formHook}
                  name="postalCode"
              />
            </>
        )}
        <ContactInfoAddress
            formHook={formHook}
            isDisableInputs={isDisableInputs}
            isSingPassUser={isSingPassUser}
            formDefaultValues={formDefaultValues}
        />
      </Accordion>
  );
};

export default ContactInformation;
